/**
 * Responsible for creating an accordion based on elements existing in the DOM
 * @module modules/accordion
 */

import $ from 'jquery';

import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'accordion';
export const CLICK_EVENT = 'click';
export const AUTO_VALUE = 'auto';
export const BLOCK_VALUE = 'block';
export const CHECKED_ATTR = 'checked';
export const FORCE_OPEN_CLASS = '.accordion__item--force-open';

// uniformSpeed: scale duration AND distance to content height so every section opens
// at the same perceived speed. Sections taller than UNIFORM_SPEED_MAX_DISTANCE (e.g.
// the ~15k px designer list) animate only up to the cap, then reveal the rest
// instantly below the fold — so they don't race.
// Hyphenated because it's interpolated into an attribute selector: a camelCased value
// would build [data-uniformSpeed], which does not match data-uniform-speed.
export const UNIFORM_SPEED_ATTR = 'uniform-speed'; // data-uniform-speed opts one in
export const UNIFORM_SPEED_PX_PER_MS = 1.3; // shared unfurl rate (pixels per millisecond)
export const UNIFORM_SPEED_MIN = 200; // ms floor so tiny sections aren't jarringly instant
export const UNIFORM_SPEED_MAX_DISTANCE = 900; // px cap on animated distance (~1 viewport)
export const UNIFORM_SPEED_DATA_KEY = 'uniformSpeedDuration'; // per-item cached duration
// Set at init on items whose content exceeds the cap, so the stylesheet can skip
// layout and paint for their off-screen rows. @see measureForUniformSpeed
export const UNIFORM_SPEED_TALL_CLASS = 'js-accordion-tall';
export const UNIFORM_SPEED_ROW_CLASS = 'js-accordion-row'; // marks one repeated row
// Measured row height, published for the stylesheet's contain-intrinsic-size. Set on
// the item and inherited by its rows, so it's one write rather than one per row.
export const UNIFORM_SPEED_ROW_HEIGHT_PROP = '--accordion-row-height';

export const DEFAULTS = {
  accItem: '.accordion__item',
  accContent: '.accordion__content',
  accActive: 'accordion__item--active',
  accRadio: 'js-accordion-radio',
  accLabel: '.accordion__label',
  accSpeed: 500,
  accToggle: false,
  accDataToggleAttr: 'toggle',
  accQueryOpen: false,
  accDataQueryAttr: 'queryString',
  accScrollToView: false, // Default to true for backward compatibility
  accDataScrollAttr: 'scroll-to-view', // Data attribute name
  uniformSpeed: false,
  onComplete: () => {},
};

/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  let opts = $.extend({}, DEFAULTS, options);
  const { accDataToggleAttr, accDataQueryAttr } = opts;

  if ($el.data(accDataToggleAttr) === true) {
    opts = $.extend({}, opts, { accToggle: true });
  }

  if ($el.data(accDataQueryAttr) === true) {
    opts = $.extend({}, opts, { accQueryOpen: true });
  }

  // Check for the scroll-to-view data attribute
  if ($el.data(opts.accDataScrollAttr) !== undefined) {
    opts = $.extend({}, opts, { accScrollToView: $el.data(opts.accDataScrollAttr) });
  }

  if ($el.data('acc-speed') !== undefined) {
    opts = $.extend({}, opts, { accSpeed: $el.data('acc-speed') });
  }

  if ($el.is(`[data-${UNIFORM_SPEED_ATTR}]`)) {
    opts = $.extend({}, opts, { uniformSpeed: true });
  }

  return opts;
};

/**
 * Caps how far a section animates so tall ones (e.g. the ~15k px designer list)
 * animate to the cap and reveal the rest on completion, instead of racing.
 * @param {number} height - Full content height in pixels
 * @return {number} Distance to animate, in pixels
 */
export const cappedDistance = height => Math.min(height, UNIFORM_SPEED_MAX_DISTANCE);

/**
 * Converts a content height into a duration so every section opens at the same
 * perceived speed: distance capped first (see cappedDistance), then floored.
 * @param {number} height - Full content height in pixels
 * @return {number} Duration in ms
 */
export const computeSpeedFromHeight = height => Math.max(
  UNIFORM_SPEED_MIN,
  Math.round(cappedDistance(height) / UNIFORM_SPEED_PX_PER_MS),
);

/**
 * Resolves an item's animation duration: the cached per-item uniformSpeed value if
 * set, else the configured speed (an explicit accSpeed of 0 always wins, for the
 * query-string "open instantly" path).
 * @private
 * @param {jQuery} $item - The accordion item being animated
 * @param {Object} options
 * @param {number} options.accSpeed - Configured/overridden speed
 * @return {number} Duration in ms
 */
const resolveSpeed = ($item, { accSpeed }) => {
  if (accSpeed === 0) {
    return 0;
  }
  const cached = $item.data(UNIFORM_SPEED_DATA_KEY);
  return cached === undefined ? accSpeed : cached;
};

/**
 * Caps very tall content to UNIFORM_SPEED_MAX_DISTANCE before collapsing so it closes
 * at the shared rate; the trimmed part is below the cap, so it isn't seen.
 * @private
 * @param {jQuery} $content - The content element(s) about to collapse
 */
const capCloseHeight = ($content) => {
  $content.get().forEach((el) => {
    const $c = $(el);
    if ($c.outerHeight(true) > UNIFORM_SPEED_MAX_DISTANCE) {
      $c.css({ height: UNIFORM_SPEED_MAX_DISTANCE });
    }
  });
};

/**
 * Handles any sort of defaults when the accordion is called upon.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 */
const applyDefaultsToActive = ($el, { accActive, accContent, accLabel }) => {
  $(`.${accActive}`, $el)
    .find(accContent)
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE,
    })
    .find(accLabel)
    .attr('aria-expanded', 'true');
};

/**
 * Handles any sort of defaults when the accordion is using radio buttons.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 */
const applyDefaultsToRadio = ($el, { accActive, accContent, accItem, accRadio }) => {
  $(`.${accRadio}:${CHECKED_ATTR}`, $el)
    .parents(accItem)
    .addClass(accActive)
    .find(accContent)
    .css({ height: AUTO_VALUE });
};

/**
 * Closes the currently active tab.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} opts - Any options provided upon initialization
 * @param  {string} opts.accLabel - Selector representing the label className
 * @param  {string} opts.accActive - Selector representing the active
 * className
 * @param  {string} opts.accContent - Selector representing the content for
 * the tab
 * @param  {string} opts.accItem - Selector representing the tab
 * @param  {boolean} opts.uniformSpeed - When true, caps very tall content before
 * collapsing so it closes at the shared rate (see capCloseHeight)
 * @see resolveSpeed for how the animation duration is determined
 */
const closeActiveTab = ($el, opts) => {
  const { accLabel, accActive, accContent, accItem } = opts;
  const $active = $(`.${accActive}`, $el);
  const $content = $active.find(accContent);
  if (opts.uniformSpeed) {
    capCloseHeight($content);
  }
  $content.stop().animate(
    { height: 0 },
    resolveSpeed($active, opts),
    function callback() {
      $(this).removeAttr('style');
    },
  );
  $el.find(accLabel).attr('aria-expanded', false);
  $el.find(accItem).removeClass(accActive);
};

/**
 * Opens a tab and provides any DOM manipulation and styles used to show a tab.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {jQuery} $parent - The parent of the interacted label
 * @param  {Object} opts - Any options provided upon initialization
 * @param  {string} opts.accLabel - Selector representing the label className
 * @param  {string} opts.accActive - Selector representing the active
 * className
 * @param  {string} opts.accContent - Selector representing the content for
 * the tab
 * @param  {string} opts.accRadio - Selector representing a radio button
 * @param  {boolean} opts.uniformSpeed - When true, animates at most
 * UNIFORM_SPEED_MAX_DISTANCE px, then reveals the remainder on completion (see
 * cappedDistance)
 * @param  {Function} opts.onComplete - Called when the animation has completed
 * @see resolveSpeed for how the animation duration is determined
 */
const openTab = ($el, $parent, opts) => {
  const { accLabel, accActive, accContent, accRadio, onComplete } = opts;
  const content = $parent.find(accContent);
  content.css({ height: AUTO_VALUE, display: BLOCK_VALUE });

  const contentHeight = content.outerHeight(true);
  // For very tall uniformSpeed sections, only animate up to the cap; the completion
  // callback below sets height:auto, revealing the remainder instantly.
  const target = opts.uniformSpeed ? cappedDistance(contentHeight) : contentHeight;

  content.css({ height: 0 }).stop().animate(
    { height: target },
    resolveSpeed($parent, opts),
    () => {
      content.css({ height: AUTO_VALUE });
      // Only scroll if data-scroll-to-view="true" is explicitly added to the element
      // First check if the parent accordion element has it
      const elScrollOption = $el.data('scroll-to-view');
      // Then check if this specific accordion item has it
      const parentScrollOption = $parent.data('scroll-to-view');
      
      // Only scroll if explicitly set to true (not just present)
      const shouldScroll = (elScrollOption === true || parentScrollOption === true);
      
      if (shouldScroll) {
        const headerOffset = $parent.offset().top;
        const fixedHeaderHeight = $('.site-header').outerHeight() || 0;
        const customOffset = parseInt($parent.data('scroll-offset') || $el.data('scroll-offset') || 0, 10);

        $('html, body').animate({
          scrollTop: headerOffset - fixedHeaderHeight - 30 - customOffset
        }, 300);
      }
      
      onComplete();
    },
  );
  $parent.find(accLabel).attr('aria-expanded', true);
  $parent
    .addClass(accActive)
    .children(accRadio).prop(CHECKED_ATTR, true);
};

/**
 * Closes an existing tab that has directly been interacted with.
 * @private
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} opts - Any options provided upon initialization
 * @param  {string} opts.accLabel - Selector representing the label className
 * @param  {string} opts.accActive - Selector representing the active
 * className
 * @param  {string} opts.accContent - Selector representing the content for
 * the tab
 * @param  {boolean} opts.uniformSpeed - When true, caps very tall content before
 * collapsing so it closes at the shared rate (see capCloseHeight)
 * @see resolveSpeed for how the animation duration is determined
 */
const closeExistingActiveTab = ($parent, opts) => {
  const { accLabel, accActive, accContent } = opts;
  const $content = $parent.find(accContent);
  if (opts.uniformSpeed) {
    capCloseHeight($content);
  }
  $content.stop().animate(
    { height: 0 },
    resolveSpeed($parent, opts),
    function callback() {
      $(this).removeAttr('style');
      $parent.removeClass(accActive);
    },
  );
  $parent.find(accLabel).attr('aria-expanded', false);
};

/**
 * At init, caches each item's height-proportional duration (read back on
 * open/close via resolveSpeed). Briefly forces each collapsed section visible to
 * measure it, then restores it synchronously so nothing flashes.
 *
 * Reuses that same measurement to flag sections taller than the animated distance
 * with UNIFORM_SPEED_TALL_CLASS, so the stylesheet only skips layout/paint for rows
 * in sections that actually have off-screen rows to skip. Also publishes a measured
 * row height for those sections, so the stylesheet's cold-start size estimate for
 * not-yet-rendered rows matches the real rows instead of a hardcoded guess.
 * @private
 * @param {jQuery} $el - The container element
 * @param {Object} options
 * @param {string} options.accItem - Selector for an accordion item
 * @param {string} options.accContent - Selector for an item's content
 */
const measureForUniformSpeed = ($el, { accItem, accContent }) => {
  $el.find(accItem).get().forEach((item) => {
    const $item = $(item);
    const content = $item.find(accContent).first();

    if (!content.length) {
      return;
    }

    // Preserve any inline styles, force the content measurable, then restore.
    const inline = { display: content[0].style.display, height: content[0].style.height };
    content.css({ display: BLOCK_VALUE, height: AUTO_VALUE });
    const contentHeight = content.outerHeight(true);
    const isTall = contentHeight > UNIFORM_SPEED_MAX_DISTANCE;
    // Measure a real row while the content is still laid out. Only tall sections get
    // containment, so only they need the height. undefined when there are no rows.
    const rowHeight = isTall
      ? content.find(`.${UNIFORM_SPEED_ROW_CLASS}`).first().outerHeight(true)
      : 0;
    content.css(inline);

    $item.data(UNIFORM_SPEED_DATA_KEY, computeSpeedFromHeight(contentHeight));
    // Both of these go on the item rather than the content: closing an item calls
    // removeAttr('style') on its content, which would wipe the custom property.
    // The item is still an ancestor of the rows, so it inherits down all the same.
    if (rowHeight) {
      $item.css(UNIFORM_SPEED_ROW_HEIGHT_PROP, `${Math.round(rowHeight)}px`);
    }
    // Flagged after measuring, so the heights above come from a true full layout
    // rather than the intrinsic-size estimate containment would report.
    $item.toggleClass(UNIFORM_SPEED_TALL_CLASS, isTall);
  });
};

/**
 * Handles the click event when a label has been iteracted with.
 * @private
 * @param {jQuery.Event} evt - A jQuery event
 * @param {HTMLElement} currentTarget - The current target interacted with
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} opts - Options passed along from initialization
 */
const itemClickHandler = ({ currentTarget }, $el, opts) => {
  const { accActive, accItem, accToggle } = opts;
  const $parent = $(currentTarget).closest(accItem);

  if (!$parent.hasClass(accActive)) {
    if (!accToggle) {
      closeActiveTab($el, opts);
    }

    openTab($el, $parent, opts);
  } else {
    closeExistingActiveTab($parent, opts);
  }
};



/**
 * Add aria-labelledby to modal content for accessible
 * audible title that corresponds with modal's heading
 * @param  {jQuery} $el
 */

const addAccContentAria = ($el) => {
  let $accLabel = $($el.find('.accordion__label'));
  if ($accLabel.length && !$accLabel.attr('id')) {
    const accContentTargetId = `accordion__content-${Math.ceil(Math.random() * 10000000000)}`;
    $accLabel.attr('id', accContentTargetId).attr('tabindex', '0');
  }
};



/**
 * Utility function to pull query parameter form URL
 * @private
 * @param {String} name - String to look for in URL
 */
const getParameterByName = (name) => {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
        results = regex.exec(location.search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

/**
 * Applies any sort of event handlers for the accordion.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
const applyEventHandlers = ($el, opts = {}) => {
   const { accLabel, accItem, accQueryOpen, accRadio } = opts;
  // check if accordion has data attr set to true
  if (accQueryOpen) {
    // check for query string in URL
    var accordionOpen = getParameterByName('accordionOpen');
  	if (accordionOpen) {
      // set parent for openTab
      const $parent = $el.find('#' + accordionOpen).closest(accItem);

      //  Set animation speed to 0 so default active tab closes as quick as possible.
      closeActiveTab($el, {...opts, accSpeed: 0});
      openTab($el, $parent, {...opts, accSpeed: 0});
    }
  }
  $el.on(CLICK_EVENT, `${opts.accItem}:not(${FORCE_OPEN_CLASS}) ${opts.accLabel}`, evt => itemClickHandler(evt, $el, opts));
  // Radio Accordion
  $el
    .find(`.${accRadio}`)
    .on(CLICK_EVENT, evt => {
      itemClickHandler(evt, $el, opts);
    });
};


/**
 * Initializes an accordion based on provided parameters.
 * @param {HTMLElement} el - An element containing the elements for the
 * accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
export default function accordion(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions($el, options);

  if (opts.uniformSpeed) {
    measureForUniformSpeed($el, opts);
  }

  addAccContentAria($el);
  applyDefaultsToActive($el, opts);
  applyDefaultsToRadio($el, opts);
  applyEventHandlers($el, opts);
}

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, accordion);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/accordion.js